import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";

import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { client } from "~/api/client";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";

export const reportsCombinedKeys = createQueryKeys("reportsCombined", {
  detail: ({ dateSeconds }: { dateSeconds: number }) => ({
    queryKey: [{ dateSeconds }]
  })
});

/**
 * `dateSeconds` is midnight in the till's timezone, so the day it belongs to is
 * only recoverable in that same timezone.
 */
export const toIsoDate = (dateSeconds: number, timezone: string) =>
  moment.tz(dateSeconds * 1000, timezone).format("YYYY-MM-DD");

function useSalesReportCombined(dateSeconds: number) {
  const { timezone } = useCurrentTillLocalisation();
  const isoDate = toIsoDate(dateSeconds, timezone);

  return useQuery({
    ...reportsCombinedKeys.detail({ dateSeconds }),
    queryFn: async () => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const res = await client.v2.pos.reportsCombined.get({
        params: {
          schoolId: auth.schoolId,
          moduleId: auth.moduleId,
          isoDate
        },
        query: { profileId: auth.profileId, tillId: auth.tillId }
      });

      if (res.status === 200) {
        return res.body;
      }

      if (res.status === 404) {
        return { reports: [], receipt: null };
      }

      throw res;
    },
    staleTime: 1000 * 30 // 30 seconds
  });
}

function useSendEmailReportCombined(dateSeconds: number) {
  const { timezone } = useCurrentTillLocalisation();
  const isoDate = toIsoDate(dateSeconds, timezone);

  return useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const res = await client.v2.pos.reportsCombined.email({
        params: {
          schoolId: auth.schoolId,
          moduleId: auth.moduleId,
          isoDate
        },
        body: { profileId: auth.profileId, email, tillId: auth.tillId }
      });

      if (res.status === 204) {
        return res.body;
      }

      throw res;
    }
  });
}

export { useSalesReportCombined, useSendEmailReportCombined };
